/**
 * Cotizaciones y pedidos.
 *
 * La decisión que sostiene todo el módulo: **las líneas guardan una copia del
 * producto, no una referencia a él**. El nombre, el precio y el impuesto
 * quedan congelados dentro del documento.
 *
 * Es deliberado y va contra el instinto de no repetir datos. Una cotización es
 * una promesa con fecha: si mañana sube el precio de la empanada, la
 * cotización que se envió ayer tiene que seguir diciendo lo que decía. Si
 * apuntara al producto, cambiaría sola y el cliente vería un número distinto
 * al que aceptó.
 */
export const MOTIVOS_PERDIDA = [
    { valor: 'precio', texto: 'Precio', nota: 'Consiguió más barato o no le cuadró el presupuesto.' },
    { valor: 'tiempo_entrega', texto: 'Tiempo de entrega', nota: 'Lo necesitaba antes de lo que se pudo ofrecer.' },
    { valor: 'otro_proveedor', texto: 'Otro proveedor', nota: 'Ya tenía con quién.' },
    { valor: 'producto', texto: 'Producto', nota: 'No teníamos lo que buscaba.' },
    { valor: 'ya_no_lo_necesita', texto: 'Ya no lo necesita', nota: 'Se cayó el evento o cambió de plan.' },
    { valor: 'no_responde', texto: 'No responde', nota: 'Se agotó el seguimiento sin respuesta. Es distinto de un no.' },
];
export const ESTADOS_COTIZACION = [
    { valor: 'borrador', texto: 'Borrador', tono: 'gris' },
    { valor: 'enviada', texto: 'Enviada', tono: 'pendiente' },
    { valor: 'ganada', texto: 'Ganada', tono: 'ok' },
    { valor: 'perdida', texto: 'Perdida', tono: 'gris' },
    { valor: 'vencida', texto: 'Vencida', tono: 'gris' },
];
/** AAAA-MM-DD en la zona de Bogotá, no en UTC. */
export function hoyBogota() {
    return new Date().toLocaleDateString('en-CA', { timeZone: 'America/Bogota' });
}
export function sumarDias(fechaISO, dias) {
    const [a, m, d] = fechaISO.split('-').map(Number);
    const f = new Date(Date.UTC(a, m - 1, d));
    f.setUTCDate(f.getUTCDate() + dias);
    return f.toISOString().slice(0, 10);
}
export function diasRestantes(vigenciaHasta) {
    const hoy = hoyBogota();
    const [a1, m1, d1] = hoy.split('-').map(Number);
    const [a2, m2, d2] = vigenciaHasta.split('-').map(Number);
    const u = Date.UTC(a1, m1 - 1, d1);
    const v = Date.UTC(a2, m2 - 1, d2);
    return Math.round((v - u) / 86400000);
}
/** "14 de septiembre de 2026" */
export function fechaLarga(fechaISO) {
    const [a, m, d] = fechaISO.split('-').map(Number);
    return new Date(Date.UTC(a, m - 1, d)).toLocaleDateString('es-CO', {
        timeZone: 'UTC', day: 'numeric', month: 'long', year: 'numeric',
    });
}
/**
 * "sábado, 11 de octubre" — con el día de la semana.
 *
 * Para los papeles que alguien lee de pie: en una comanda, el día de la semana
 * se reconoce de un vistazo y el número no. Se arma con `Date.UTC` y se lee en
 * UTC por la misma razón que `fechaLarga`: `new Date('2026-10-11')` es
 * medianoche UTC, que en Bogotá es el 10 a las 7 de la noche, y la comanda
 * saldría con el día anterior.
 */
export function fechaConDia(fechaISO) {
    const [a, m, d] = fechaISO.split('-').map(Number);
    if (!a || !m || !d)
        return fechaISO;
    return new Date(Date.UTC(a, m - 1, d)).toLocaleDateString('es-CO', {
        timeZone: 'UTC', weekday: 'long', day: 'numeric', month: 'long',
    });
}
/**
 * La llave del contador del día: cot-2026-0914.
 * El consecutivo se reinicia cada día, como en el formato del negocio.
 */
export function llaveContador(prefijo, fechaISO) {
    return `${prefijo}-${fechaISO.slice(0, 4)}-${fechaISO.slice(5, 7)}${fechaISO.slice(8, 10)}`;
}
export function numeroDocumento(prefijo, fechaISO, consecutivo) {
    const aaaa = fechaISO.slice(0, 4);
    const mmdd = fechaISO.slice(5, 7) + fechaISO.slice(8, 10);
    return `${prefijo}-${aaaa}-${mmdd}-${String(consecutivo).padStart(2, '0')}`;
}
/** "cuatrocientos ocho mil pesos m/cte." — para el pie del documento. */
export function enLetras(n) {
    const U = ['', 'uno', 'dos', 'tres', 'cuatro', 'cinco', 'seis', 'siete', 'ocho', 'nueve',
        'diez', 'once', 'doce', 'trece', 'catorce', 'quince', 'dieciséis', 'diecisiete',
        'dieciocho', 'diecinueve', 'veinte', 'veintiuno', 'veintidós', 'veintitrés',
        'veinticuatro', 'veinticinco', 'veintiséis', 'veintisiete', 'veintiocho', 'veintinueve'];
    const D = ['', '', '', 'treinta', 'cuarenta', 'cincuenta', 'sesenta', 'setenta', 'ochenta', 'noventa'];
    const C = ['', 'ciento', 'doscientos', 'trescientos', 'cuatrocientos', 'quinientos',
        'seiscientos', 'setecientos', 'ochocientos', 'novecientos'];
    function hasta999(x) {
        if (x === 0)
            return '';
        if (x === 100)
            return 'cien';
        const c = Math.floor(x / 100);
        const r = x % 100;
        const centena = C[c] ?? '';
        let resto = '';
        if (r < 30)
            resto = U[r] ?? '';
        else {
            const d = Math.floor(r / 10);
            const u = r % 10;
            resto = u ? `${D[d]} y ${U[u]}` : (D[d] ?? '');
        }
        return [centena, resto].filter(Boolean).join(' ');
    }
    // Antes de "mil" o "millones" el uno se apocopa: veintiún mil, no
    // veintiuno mil; treinta y un mil, no treinta y uno mil.
    const apocope = (t) => t.replace(/veintiuno$/, 'veintiún').replace(/\buno$/, 'un');
    if (n === 0)
        return 'cero pesos m/cte.';
    const millones = Math.floor(n / 1_000_000);
    const miles = Math.floor((n % 1_000_000) / 1000);
    const resto = n % 1000;
    const partes = [];
    if (millones === 1)
        partes.push('un millón');
    else if (millones > 1)
        partes.push(`${apocope(hasta999(millones))} millones`);
    if (miles === 1)
        partes.push('mil');
    else if (miles > 1)
        partes.push(`${apocope(hasta999(miles))} mil`);
    if (resto > 0)
        partes.push(hasta999(resto));
    return `${partes.join(' ')} pesos m/cte.`;
}
